/**
 * Tuner Engine — Chromatic instrument tuner
 * Mic-based pitch detection, tone generator, strobe + overtone visualisers.
 */

(function () {
  'use strict';

  // ── Constants ──────────────────────────────────────────────────────────────

  const STORAGE_KEY = 'sa-tool-tuner';
  const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const IN_TUNE_THRESHOLD = 5; // cents window — readable on phone

  // Clamp A4 reference between 438–445 Hz
  const REF_MIN = 438;
  const REF_MAX = 445;

  // Instrument string tunings (note-name + octave string)
  const TUNINGS = {
    guitar6: {
      standard: ['E4', 'B3', 'G3', 'D3', 'A2', 'E2'],
      dropD:    ['E4', 'B3', 'G3', 'D3', 'A2', 'D2'],
      dropC:    ['D4', 'A3', 'F3', 'C3', 'G2', 'C2'],
      dadgad:   ['D4', 'A3', 'G3', 'D3', 'A2', 'D2'],
      openG:    ['D4', 'B3', 'G3', 'D3', 'G2', 'D2'],
      openD:    ['D4', 'A3', 'F#3', 'D3', 'A2', 'D2'],
      halfDown: ['Eb4', 'Bb3', 'Gb3', 'Db3', 'Ab2', 'Eb2'],
    },
    guitar7: {
      standard: ['E4', 'B3', 'G3', 'D3', 'A2', 'E2', 'B1'],
      dropA:    ['E4', 'B3', 'G3', 'D3', 'A2', 'D2', 'A1'],
    },
    guitar8: {
      standard: ['E4', 'B3', 'G3', 'D3', 'A2', 'E2', 'B1', 'F#1'],
    },
    bass4: {
      standard: ['G2', 'D2', 'A1', 'E1'],
      dropD:    ['G2', 'D2', 'A1', 'D1'],
    },
    bass5: {
      standard: ['G2', 'D2', 'A1', 'E1', 'B0'],
    },
    ukulele: {
      standard: ['A4', 'E4', 'C4', 'G4'],
    },
    violin: {
      standard: ['E5', 'A4', 'D4', 'G3'],
    },
    chromatic: {
      standard: [],
    },
  };

  // Tuning preset label map for the UI select
  const TUNING_LABELS = {
    standard: 'Standard',
    dropD:    'Drop D',
    dropC:    'Drop C',
    dadgad:   'DADGAD',
    openG:    'Open G',
    openD:    'Open D',
    halfDown: 'Half Step Down',
    dropA:    'Drop A',
    standard_bass5: 'Standard',
    dropD_bass4: 'Drop D',
  };

  // ── State defaults ─────────────────────────────────────────────────────────

  function defaultState() {
    return {
      ref440:      440,
      instrument:  'guitar6',
      tuningPreset:'standard',
      transposition: 0,
      waveform:    'sine',
      currentOctave: 4,
      currentMode: 'standard',
    };
  }

  // ── TunerEngine class ──────────────────────────────────────────────────────

  class TunerEngine {
    constructor() {
      this.state = Object.assign(defaultState(), this._loadSettings());

      // Runtime (not persisted)
      this.isListening  = false;
      this.currentFreq  = 0;
      this.currentCents = 0;
      // Ring buffer — use NaN as "no reading yet" to avoid biasing the average toward 0
      this.centsBuffer  = new Array(8).fill(NaN);

      // Audio nodes
      this.audioCtx    = null;
      this.analyser    = null;
      this.mediaStream = null;
      this.animationId = null;
      this.strobeAnimId = null;
      this.overtoneAnimId = null;
      this.strobePhase = 0;
      this.timeDomainBuffer = null;
      this.frequencyDataBuffer = null;
      this.lastPitchTimestamp = 0;
      this.pitchIntervalMs = 1000 / 30;

      // Tone generator
      this.toneOsc  = null;
      this.toneGain = null;
      this.playingNote = null;
    }

    // ── Persistence ───────────────────────────────────────────────────────

    _loadSettings() {
      try {
        const raw = window.flPreferenceStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
      } catch (_) {}
      return {};
    }

    _saveSettings() {
      try {
        window.flPreferenceStorage.setItem(STORAGE_KEY, JSON.stringify({
          ref440:       this.state.ref440,
          instrument:   this.state.instrument,
          tuningPreset: this.state.tuningPreset,
          transposition:this.state.transposition,
          waveform:     this.state.waveform,
          currentOctave:this.state.currentOctave,
          currentMode:  this.state.currentMode,
        }));
      } catch (_) {}
    }

    // ── Init ──────────────────────────────────────────────────────────────

    init() {
      this._applyRestoredState();
      this._bindEvents();
      this._bindKeyboard();
      this._bindVisibility();
      this._populateTuningSelect();
      this._updatePolyDisplay();
    }

    /** Apply restored settings to DOM controls */
    _applyRestoredState() {
      const s = this.state;

      const instrEl = document.getElementById('InstrumentSelect');
      if (instrEl) instrEl.value = s.instrument;

      this._populateTuningSelect();

      const tuningEl = document.getElementById('TuningSelect');
      if (tuningEl) tuningEl.value = s.tuningPreset;

      const pitchEl = document.getElementById('PitchValue');
      if (pitchEl) pitchEl.textContent = s.ref440 + ' Hz';

      const transpEl = document.getElementById('TransposeSelect');
      if (transpEl) transpEl.value = String(s.transposition);

      // Waveform
      document.querySelectorAll('.wave-btn').forEach(btn => {
        const active = btn.dataset.wave === s.waveform;
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      // Octave
      document.querySelectorAll('.octave-btn').forEach(btn => {
        const active = parseInt(btn.dataset.octave) === s.currentOctave;
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', String(active));
      });

      // Mode tabs
      document.querySelectorAll('[data-mode]').forEach(tab => {
        const active = tab.dataset.mode === s.currentMode;
        tab.classList.toggle('active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      this._showModePanel();
    }

    // ── Pitch detection ───────────────────────────────────────────────────

    async startListening() {
      if (this.isListening) return;
      try {
        // Create (or resume) AudioContext on user gesture
        if (!this.audioCtx) {
          this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (this.audioCtx.state === 'suspended') {
          await this.audioCtx.resume();
        }

        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            autoGainControl: false,
            noiseSuppression: false,
          },
        });

        this.analyser = this.audioCtx.createAnalyser();
        this.analyser.fftSize = 4096;
        this.timeDomainBuffer = new Float32Array(this.analyser.fftSize);
        this.frequencyDataBuffer = new Uint8Array(this.analyser.frequencyBinCount);
        this.lastPitchTimestamp = 0;

        const source = this.audioCtx.createMediaStreamSource(this.mediaStream);
        source.connect(this.analyser);

        this.isListening = true;
        this.centsBuffer.fill(NaN);
        this._updateMicBtn();
        this._announce('Tuner listening');
        this._detectPitch();

        if (this.state.currentMode === 'strobe') this._animateStrobe();
        if (this.state.currentMode === 'overtone') this._animateOvertones();

      } catch (err) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          const denied = document.getElementById('MicDeniedMsg');
          if (denied) denied.hidden = false;
          this._announce('Microphone access was denied. Allow mic access in your browser settings and reload the page.');
        } else {
          this._announce('Could not access microphone: ' + err.message);
          console.warn('Tuner mic error:', err);
        }
      }
    }

    stopListening() {
      if (!this.isListening) return;

      // Set isListening first so any in-flight rAF callbacks exit immediately
      this.isListening = false;

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(t => t.stop());
        this.mediaStream = null;
      }

      cancelAnimationFrame(this.animationId);
      cancelAnimationFrame(this.strobeAnimId);
      cancelAnimationFrame(this.overtoneAnimId);
      this.animationId = null;
      this.strobeAnimId = null;
      this.overtoneAnimId = null;
      this.timeDomainBuffer = null;
      this.frequencyDataBuffer = null;
      this._resetDisplay();
      this._updateMicBtn();
      this._announce('Tuner stopped');
    }

    _detectPitch(timestamp = performance.now()) {
      if (!this.isListening) return;

      if (timestamp - this.lastPitchTimestamp < this.pitchIntervalMs) {
        this.animationId = requestAnimationFrame(nextTimestamp => this._detectPitch(nextTimestamp));
        return;
      }
      this.lastPitchTimestamp = timestamp;

      const buffer = this.timeDomainBuffer || new Float32Array(this.analyser.fftSize);
      this.timeDomainBuffer = buffer;
      this.analyser.getFloatTimeDomainData(buffer);

      const freq = this._autoCorrelate(buffer, this.audioCtx.sampleRate);

      if (freq > 0) {
        this.currentFreq = freq;
        const noteData = this._freqToNote(freq);

        // Smooth cents with a moving average, ignoring empty slots (NaN)
        this.centsBuffer.push(noteData.cents);
        this.centsBuffer.shift();
        const validReadings = this.centsBuffer.filter(v => !isNaN(v));
        const smoothed = validReadings.length > 0
          ? validReadings.reduce((a, b) => a + b, 0) / validReadings.length
          : noteData.cents;
        noteData.cents = smoothed;
        this.currentCents = smoothed;

        this._updateDisplay(noteData);

        if (this.state.currentMode === 'polyphonic') {
          this._updatePolyMeters(freq);
        }
      }

      this.animationId = requestAnimationFrame(nextTimestamp => this._detectPitch(nextTimestamp));
    }

    /**
     * Robust autocorrelation (AMDFish variant for stability).
     * Returns detected frequency in Hz, or -1 if signal is too weak / no clear pitch.
     */
    _autoCorrelate(buffer, sampleRate) {
      const SIZE = buffer.length;
      const HALF = Math.floor(SIZE / 2);

      // RMS gating — skip if signal too quiet
      let rms = 0;
      for (let i = 0; i < SIZE; i++) rms += buffer[i] * buffer[i];
      rms = Math.sqrt(rms / SIZE);
      if (rms < 0.01) return -1;

      // Normalised autocorrelation (r[tau] / r[0])
      // Compute r[0] first
      let r0 = 0;
      for (let i = 0; i < HALF; i++) r0 += buffer[i] * buffer[i];
      if (r0 < 1e-10) return -1;

      let bestTau = -1;
      let bestVal = 0;
      let foundPeak = false;

      // Skip the trivial zero-lag peak; look for the first strong peak after it
      for (let tau = 1; tau < HALF; tau++) {
        let r = 0;
        for (let i = 0; i < HALF; i++) {
          r += buffer[i] * buffer[i + tau];
        }
        const norm = r / r0;

        if (norm > 0.9 && norm > bestVal) {
          bestVal = norm;
          bestTau = tau;
          foundPeak = true;
        } else if (foundPeak && norm < bestVal * 0.8) {
          // Descended from peak — done searching
          break;
        }
      }

      if (bestTau < 1 || bestVal < 0.9) return -1;

      // Parabolic interpolation for sub-sample accuracy
      const prev = bestTau > 1 ? this._autocorrAt(buffer, HALF, bestTau - 1) / r0 : bestVal;
      const next = bestTau < HALF - 1 ? this._autocorrAt(buffer, HALF, bestTau + 1) / r0 : bestVal;
      const denom = 2 * bestVal - prev - next;
      const refinedTau = denom !== 0 ? bestTau + (next - prev) / (2 * denom) : bestTau;

      return sampleRate / refinedTau;
    }

    _autocorrAt(buffer, half, tau) {
      let r = 0;
      for (let i = 0; i < half; i++) r += buffer[i] * buffer[i + tau];
      return r;
    }

    _freqToNote(freq) {
      // Semitones from A4 = ref440
      const noteNum = 12 * Math.log2(freq / this.state.ref440);
      const rounded = Math.round(noteNum);
      const cents   = (noteNum - rounded) * 100;

      // MIDI note number (A4 = 69)
      const midi = rounded + 69;
      // Apply transposition for display only
      const dispMidi = midi - this.state.transposition;
      const noteIdx  = ((dispMidi % 12) + 12) % 12;
      const octave   = Math.floor(dispMidi / 12) - 1;

      return { note: NOTES[noteIdx], octave, cents };
    }

    // ── Display updates ───────────────────────────────────────────────────

    _updateDisplay(noteData) {
      const { note, octave, cents } = noteData;
      const abs = Math.abs(cents);
      const inTune = abs <= IN_TUNE_THRESHOLD;
      const sharp  = cents > IN_TUNE_THRESHOLD;

      // State class helper
      const stateClass = inTune ? 'in-tune' : sharp ? 'sharp' : 'flat';

      const noteEl = document.getElementById('CurrentNote');
      if (noteEl) {
        noteEl.textContent = note;
        noteEl.className = 'current-note ' + stateClass;
      }

      const octaveEl = document.getElementById('NoteOctave');
      if (octaveEl) octaveEl.textContent = octave;

      const centsEl = document.getElementById('CentsValue');
      if (centsEl) {
        const sign = cents > 0 ? '+' : '';
        centsEl.textContent = sign + cents.toFixed(1) + ' ¢';
      }

      const needleEl = document.getElementById('CentsNeedle');
      if (needleEl) {
        // Map −50…+50 cents → 0…100%
        const pct = Math.max(0, Math.min(100, 50 + cents));
        needleEl.style.left = pct + '%';
        needleEl.className = 'cents-needle ' + stateClass;
      }

      const statusPill = document.getElementById('TuningStatus');
      if (statusPill) {
        const textEl = statusPill.querySelector('.status-text');
        statusPill.className = 'tuning-status-pill ' + stateClass;
        if (textEl) {
          textEl.textContent = inTune ? 'In tune' : sharp ? 'Too sharp' : 'Too flat';
        }
        // Remove any leftover inline styles from old code
        statusPill.style.borderColor = '';
        statusPill.style.boxShadow   = '';
      }

      const freqEl = document.getElementById('FreqValue');
      if (freqEl) freqEl.textContent = this.currentFreq.toFixed(1) + ' Hz';
    }

    _resetDisplay() {
      const noteEl = document.getElementById('CurrentNote');
      if (noteEl) { noteEl.textContent = '—'; noteEl.className = 'current-note'; }

      const octaveEl = document.getElementById('NoteOctave');
      if (octaveEl) octaveEl.textContent = '';

      const centsEl = document.getElementById('CentsValue');
      if (centsEl) centsEl.textContent = '— ¢';

      const needleEl = document.getElementById('CentsNeedle');
      if (needleEl) { needleEl.style.left = '50%'; needleEl.className = 'cents-needle'; }

      const statusPill = document.getElementById('TuningStatus');
      if (statusPill) {
        statusPill.className = 'tuning-status-pill';
        statusPill.style.borderColor = '';
        statusPill.style.boxShadow   = '';
        const textEl = statusPill.querySelector('.status-text');
        if (textEl) textEl.textContent = 'Play a note';
      }

      const freqEl = document.getElementById('FreqValue');
      if (freqEl) freqEl.textContent = '— Hz';
    }

    _updateMicBtn() {
      const btn = document.getElementById('MicBtn');
      if (!btn) return;
      const textEl = btn.querySelector('.mic-btn__text');
      if (textEl) textEl.textContent = this.isListening ? 'Stop Tuning' : 'Start Tuning';
      btn.setAttribute('aria-pressed', String(this.isListening));
    }

    _announce(msg) {
      const el = document.getElementById('TunerAnnounce');
      if (el) el.textContent = msg;
    }

    // ── Mode switching ────────────────────────────────────────────────────

    _showModePanel() {
      const mode = this.state.currentMode;
      const ids  = ['StandardDisplay', 'StrobeDisplay', 'PolyDisplay', 'OvertoneDisplay'];
      ids.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.hidden = true;
      });

      const visContainer = document.getElementById('VisualizerContainer');
      const isVisMode = mode !== 'standard';
      if (visContainer) visContainer.hidden = !isVisMode;

      const standardEl = document.getElementById('StandardDisplay');
      if (standardEl) standardEl.hidden = (mode !== 'standard');

      if (mode === 'strobe') {
        const el = document.getElementById('StrobeDisplay');
        if (el) el.hidden = false;
        if (this.isListening) this._animateStrobe();
      } else if (mode === 'polyphonic') {
        const el = document.getElementById('PolyDisplay');
        if (el) el.hidden = false;
      } else if (mode === 'overtone') {
        const el = document.getElementById('OvertoneDisplay');
        if (el) el.hidden = false;
        if (this.isListening) this._animateOvertones();
      }
    }

    // ── Polyphonic display ────────────────────────────────────────────────

    _populateTuningSelect() {
      const instrKey  = this.state.instrument;
      const tuningMap = TUNINGS[instrKey] || {};
      const select    = document.getElementById('TuningSelect');
      if (!select) return;

      select.innerHTML = '';
      const keys = Object.keys(tuningMap);
      if (keys.length === 0) {
        const opt = document.createElement('option');
        opt.value = 'standard'; opt.textContent = 'Chromatic (all notes)';
        select.appendChild(opt);
      } else {
        keys.forEach(k => {
          const opt = document.createElement('option');
          opt.value = k;
          opt.textContent = TUNING_LABELS[k] || k;
          select.appendChild(opt);
        });
      }

      // Try to restore saved preset, fall back to first
      const desired = this.state.tuningPreset;
      if (tuningMap[desired]) {
        select.value = desired;
      } else {
        this.state.tuningPreset = keys[0] || 'standard';
        select.value = this.state.tuningPreset;
      }
    }

    _updatePolyDisplay() {
      const container = document.getElementById('PolyStringsContainer');
      if (!container) return;

      const instrKey  = this.state.instrument;
      const tuningMap = TUNINGS[instrKey] || {};
      const strings   = tuningMap[this.state.tuningPreset] || [];

      container.innerHTML = '';
      strings.forEach(noteStr => {
        const d = document.createElement('div');
        d.className = 'poly-string';
        const label = noteStr.replace(/[0-9]/g, '');
        d.innerHTML =
          '<span class="poly-note">' + label + '</span>' +
          '<div class="poly-meter"><div class="poly-fill"></div></div>';
        container.appendChild(d);
      });
    }

    /**
     * Live-update the per-string poly-fill meters from a detected frequency.
     * For each string, compute cents deviation from its target frequency and
     * shift the fill bar left/right within the meter track.
     */
    _updatePolyMeters(detectedFreq) {
      const container = document.getElementById('PolyStringsContainer');
      if (!container || detectedFreq <= 0) return;

      const instrKey  = this.state.instrument;
      const tuningMap = TUNINGS[instrKey] || {};
      const strings   = tuningMap[this.state.tuningPreset] || [];
      const fills     = container.querySelectorAll('.poly-fill');

      // Build reference frequencies for every string (applying transposition)
      strings.forEach((noteStr, i) => {
        const fill = fills[i];
        if (!fill) return;

        // Parse "E4", "Bb3", "F#3" etc into a frequency
        const targetFreq = this._noteStrToFreq(noteStr);
        if (targetFreq <= 0) return;

        // Cents deviation of detected pitch from this string's target
        const centsFromString = 1200 * Math.log2(detectedFreq / targetFreq);

        // Only light up the closest string (within ±60 cents); others go neutral
        const absC = Math.abs(centsFromString);
        if (absC > 60) {
          // Too far away — leave bar centred (neutral)
          fill.style.left  = '50%';
          fill.style.width = '4px';
          fill.style.background = 'var(--tn-accent)';
          return;
        }

        // Map −50…+50 cents → 5%…95% of the meter width
        const pct = Math.max(5, Math.min(95, 50 + centsFromString));
        fill.style.left  = pct + '%';

        // Widen the fill a bit when sharp/flat so it's visually salient
        const inTune = absC <= IN_TUNE_THRESHOLD;
        fill.style.width = inTune ? '6px' : '4px';
        fill.style.background = inTune
          ? 'var(--tn-in-tune)'
          : centsFromString > 0
            ? 'var(--tn-sharp)'
            : 'var(--tn-flat)';
      });
    }

    /**
     * Convert a note string like "E4", "Bb3", "F#3", "Eb2" to Hz.
     * Uses the same ref440 as the rest of the engine.
     */
    _noteStrToFreq(noteStr) {
      // Strip octave number and note name
      const match = noteStr.match(/^([A-G][b#]?)(\d+)$/);
      if (!match) return -1;

      const name   = match[1];
      const octave = parseInt(match[2], 10);

      // Normalise flat spellings to sharp equivalents
      const flatMap = { 'Ab': 'G#', 'Bb': 'A#', 'Cb': 'B', 'Db': 'C#', 'Eb': 'D#', 'Fb': 'E', 'Gb': 'F#' };
      const norm    = flatMap[name] || name;
      const idx     = NOTES.indexOf(norm);
      if (idx < 0) return -1;

      // MIDI note number
      const midi = (octave + 1) * 12 + idx;
      // Apply transposition (same offset used in _freqToNote display)
      const sndMidi = midi + this.state.transposition;
      return this.state.ref440 * Math.pow(2, (sndMidi - 69) / 12);
    }

    // ── Visualisers ───────────────────────────────────────────────────────

    _animateStrobe() {
      if (this.strobeAnimId) cancelAnimationFrame(this.strobeAnimId);
      const canvas = document.getElementById('StrobeCanvas');
      if (!canvas) return;

      const ctx = canvas.getContext('2d');

      const draw = () => {
        if (!this.isListening || this.state.currentMode !== 'strobe') return;

        const dpr = window.devicePixelRatio || 1;
        const cw  = canvas.clientWidth;
        const ch  = canvas.clientHeight;
        if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
          canvas.width  = Math.round(cw * dpr);
          canvas.height = Math.round(ch * dpr);
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }
        const w = cw, h = ch;

        // Clear
        ctx.fillStyle = '#141311';
        ctx.fillRect(0, 0, w, h);

        // Move phase proportional to cents offset
        // In tune (0 cents) → phase holds still
        this.strobePhase -= this.currentCents * 0.04;

        const barCount = 14;
        const barW     = w / barCount;
        const offset   = ((this.strobePhase % barW) + barW) % barW;
        const inTune   = Math.abs(this.currentCents) <= IN_TUNE_THRESHOLD;

        for (let i = -1; i < barCount + 2; i++) {
          const x = i * barW + offset;
          // Alternate bars
          if (i % 2 === 0) {
            ctx.fillStyle = inTune
              ? 'rgba(227,87,40,0.65)'     // HEADLINER orange lock state
              : 'rgba(236,226,207,0.18)';  // muted bone
            ctx.fillRect(x, 0, barW * 0.85, h);
          }
        }

        // Centre target line
        ctx.fillStyle = 'rgba(236,226,207,0.7)';
        ctx.fillRect(w / 2 - 1, 0, 2, h);

        this.strobeAnimId = requestAnimationFrame(draw);
      };
      draw();
    }

    _animateOvertones() {
      if (this.overtoneAnimId) cancelAnimationFrame(this.overtoneAnimId);
      const canvas = document.getElementById('OvertoneCanvas');
      if (!canvas) return;

      const ctx = canvas.getContext('2d');

      const draw = () => {
        if (!this.isListening || this.state.currentMode !== 'overtone') return;

        const dpr = window.devicePixelRatio || 1;
        const cw  = canvas.clientWidth;
        const ch  = canvas.clientHeight;
        if (canvas.width !== Math.round(cw * dpr) || canvas.height !== Math.round(ch * dpr)) {
          canvas.width  = Math.round(cw * dpr);
          canvas.height = Math.round(ch * dpr);
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }
        const w = cw, h = ch;

        const freqData = this.frequencyDataBuffer || new Uint8Array(this.analyser.frequencyBinCount);
        this.frequencyDataBuffer = freqData;
        this.analyser.getByteFrequencyData(freqData);

        ctx.fillStyle = '#141311';
        ctx.fillRect(0, 0, w, h);

        const bins    = Math.min(freqData.length, 256);
        const barW    = w / bins;
        const nyquist = this.audioCtx.sampleRate / 2;

        for (let i = 0; i < bins; i++) {
          const val    = freqData[i] / 255;
          const barH   = val * h;
          const binHz  = (i / freqData.length) * nyquist;

          // Highlight fundamental and harmonics with accent colour, rest muted
          let isHarmonic = false;
          if (this.currentFreq > 0 && binHz > 0) {
            const ratio = binHz / this.currentFreq;
            const nearInt = Math.round(ratio);
            if (nearInt >= 1 && Math.abs(ratio - nearInt) < 0.04) isHarmonic = true;
          }

          ctx.fillStyle = isHarmonic
            ? 'rgba(227,87,40,0.85)'    // HEADLINER orange — fundamental/harmonics
            : 'rgba(236,226,207,0.12)'; // muted — other content
          ctx.fillRect(i * barW, h - barH, barW - 1, barH);
        }

        // Update info labels
        const fundEl  = document.getElementById('FundamentalFreq');
        const harmEl  = document.getElementById('HarmonicsCount');
        if (this.currentFreq > 0) {
          if (fundEl) fundEl.textContent = this.currentFreq.toFixed(1) + ' Hz';
          // Count visible harmonic peaks
          let count = 0;
          for (let n = 2; n <= 8; n++) {
            const hHz = this.currentFreq * n;
            const bin = Math.round((hHz / nyquist) * freqData.length);
            if (bin < freqData.length && freqData[bin] > 60) count++;
          }
          if (harmEl) harmEl.textContent = count;
        } else {
          if (fundEl) fundEl.textContent = '—';
          if (harmEl) harmEl.textContent = '—';
        }

        this.overtoneAnimId = requestAnimationFrame(draw);
      };
      draw();
    }

    // ── Tone generator ────────────────────────────────────────────────────

    async _playTone(noteStr) {
      this._stopTone();

      this.playingNote = noteStr;
      this._updateToneDisplay();

      // AudioContext must be created/resumed on user gesture — we are inside a click handler here
      if (!this.audioCtx) {
        this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (this.audioCtx.state === 'suspended') await this.audioCtx.resume();

      const noteIdx  = NOTES.indexOf(noteStr);
      if (noteIdx < 0) return;

      // Transposition: pressing "C" on a Bb instrument (−2) sounds D (concert)
      const midi    = (this.state.currentOctave + 1) * 12 + noteIdx;
      const sndMidi = midi + this.state.transposition;
      const freq    = this.state.ref440 * Math.pow(2, (sndMidi - 69) / 12);

      const freqEl = document.getElementById('TonePlayingFreq');
      if (freqEl) freqEl.textContent = freq.toFixed(1) + ' Hz';

      this.toneOsc  = this.audioCtx.createOscillator();
      this.toneGain = this.audioCtx.createGain();

      this.toneOsc.type = this.state.waveform;
      this.toneOsc.frequency.value = freq;

      const now = this.audioCtx.currentTime;
      this.toneGain.gain.setValueAtTime(0, now);
      this.toneGain.gain.linearRampToValueAtTime(0.2, now + 0.08);

      this.toneOsc.connect(this.toneGain);
      this.toneGain.connect(this.audioCtx.destination);
      this.toneOsc.start();

      // Update aria-pressed on note buttons
      document.querySelectorAll('.note-btn').forEach(btn => {
        const active = btn.dataset.note === noteStr;
        btn.setAttribute('aria-pressed', String(active));
      });
    }

    _stopTone() {
      if (this.toneOsc && this.toneGain && this.audioCtx) {
        const now = this.audioCtx.currentTime;
        this.toneGain.gain.cancelScheduledValues(now);
        this.toneGain.gain.setValueAtTime(this.toneGain.gain.value, now);
        this.toneGain.gain.linearRampToValueAtTime(0, now + 0.08);
        const osc = this.toneOsc;
        setTimeout(() => { try { osc.stop(); } catch (_) {} }, 120);
        this.toneOsc  = null;
        this.toneGain = null;
      }

      this.playingNote = null;
      document.querySelectorAll('.note-btn').forEach(b => b.setAttribute('aria-pressed', 'false'));
      const freqEl = document.getElementById('TonePlayingFreq');
      if (freqEl) freqEl.textContent = '';
      this._updateToneDisplay();
    }

    _stopAllAudio() {
      this.stopListening();
      this._stopTone();
    }

    _updateToneDisplay() {
      const el = document.getElementById('TonePlayingNote');
      if (el) el.textContent = this.playingNote ? this.playingNote + this.state.currentOctave : '—';
    }

    // ── Event binding ─────────────────────────────────────────────────────

    _bindEvents() {
      // Mic toggle
      document.getElementById('MicBtn')?.addEventListener('click', () => {
        this.isListening ? this.stopListening() : this.startListening();
      });

      // Instrument select
      document.getElementById('InstrumentSelect')?.addEventListener('change', e => {
        this.state.instrument = e.target.value;
        this._populateTuningSelect();
        this._updatePolyDisplay();
        this._saveSettings();
      });

      // Tuning preset
      document.getElementById('TuningSelect')?.addEventListener('change', e => {
        this.state.tuningPreset = e.target.value;
        this._updatePolyDisplay();
        this._saveSettings();
      });

      // Reference pitch stepper
      document.getElementById('PitchDown')?.addEventListener('click', () => {
        if (this.state.ref440 > REF_MIN) {
          this.state.ref440--;
          const el = document.getElementById('PitchValue');
          if (el) el.textContent = this.state.ref440 + ' Hz';
          this._saveSettings();
        }
      });
      document.getElementById('PitchUp')?.addEventListener('click', () => {
        if (this.state.ref440 < REF_MAX) {
          this.state.ref440++;
          const el = document.getElementById('PitchValue');
          if (el) el.textContent = this.state.ref440 + ' Hz';
          this._saveSettings();
        }
      });

      // Transposition
      document.getElementById('TransposeSelect')?.addEventListener('change', e => {
        this.state.transposition = parseInt(e.target.value, 10);
        this._saveSettings();
      });

      // Mode tabs
      document.querySelectorAll('[data-mode]').forEach(tab => {
        tab.addEventListener('click', () => {
          this.state.currentMode = tab.dataset.mode;
          document.querySelectorAll('[data-mode]').forEach(t => {
            const active = t === tab;
            t.classList.toggle('active', active);
            t.setAttribute('aria-selected', String(active));
            t.setAttribute('tabindex', active ? '0' : '-1');
          });
          this._showModePanel();
          this._saveSettings();
        });

        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          const tabs = Array.from(document.querySelectorAll('[data-mode]'));
          const currentIndex = tabs.indexOf(tab);
          if (currentIndex < 0) return;
          event.preventDefault();
          let nextIndex = currentIndex;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
          tabs[nextIndex].click();
          tabs[nextIndex].focus();
        });
      });

      // Tone generator: note buttons
      document.querySelectorAll('.note-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const note = btn.dataset.note;
          if (this.playingNote === note) {
            this._stopTone();
          } else {
            this._playTone(note);
          }
        });
      });

      // Octave selector
      document.querySelectorAll('.octave-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          this.state.currentOctave = parseInt(btn.dataset.octave, 10);
          document.querySelectorAll('.octave-btn').forEach(b => {
            const active = b === btn;
            b.classList.toggle('active', active);
            b.setAttribute('aria-pressed', String(active));
          });
          // Restart tone at new octave if one is playing
          if (this.playingNote) this._playTone(this.playingNote);
          this._saveSettings();
        });
      });

      // Stop tone button
      document.getElementById('StopToneBtn')?.addEventListener('click', () => this._stopTone());

      // Waveform buttons
      document.querySelectorAll('.wave-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          this.state.waveform = btn.dataset.wave;
          document.querySelectorAll('.wave-btn').forEach(b => {
            const active = b === btn;
            b.classList.toggle('active', active);
            b.setAttribute('aria-pressed', String(active));
          });
          if (this.toneOsc) this.toneOsc.type = this.state.waveform;
          this._saveSettings();
        });
      });

    }

    _bindKeyboard() {
      document.addEventListener('keydown', e => {
        const active = document.activeElement;
        if (active && active.closest && active.closest('a, button, input, textarea, select, summary, [contenteditable="true"], [role="dialog"]')) return;
        if (document.querySelector('[role="dialog"][aria-hidden="false"], [role="dialog"].is-open, [role="dialog"].open, [role="dialog"].active')) return;
        if (!document.querySelector('[data-tuner-app]')) return;

        if (e.key === ' ' || e.code === 'Space') {
          e.preventDefault(); // guard against page scroll
          this.isListening ? this.stopListening() : this.startListening();
        }
      });
    }

    _bindVisibility() {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this._stopAllAudio();
      });
    }
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  function init() {
    if (!document.querySelector('[data-tuner-app]')) return;
    window.TunerEngine = new TunerEngine();
    window.TunerEngine.init();
  }

  function boot() {
    window.flRunWhenPrivacyReady(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
